import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { motion, useReducedMotion } from 'motion/react';
import { Logo } from '@/components/Logo';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';

import { tr } from '@/i18n';
/** Раскладка страниц входа: форма слева, живая иллюстрация доски справа. */
export function AuthLayout({ title, subtitle, children, footer }: { title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="grid min-h-full lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <div className="flex flex-col px-6 py-6 sm:px-10">
        <div className="flex items-center justify-between">
          <Link to="/" aria-label={tr("На главную")}>
            <Logo />
          </Link>
          <ThemeSwitcher />
        </div>
        <main id="main" tabIndex={-1} className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-12">
          <h1 className="text-[28px] font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-fg-muted">{subtitle}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-8 text-sm text-fg-muted">{footer}</div>}
        </main>
      </div>
      <BoardIllustration />
    </div>
  );
}

const STICKIES = [
  { x: 14, y: 16, r: -4, c: '#FFF1A8', t: tr("Идеи на неделю") },
  { x: 46, y: 10, r: 3, c: '#FFC9DE', t: tr("Ретро в пятницу") },
  { x: 70, y: 28, r: -2, c: '#C7E4FF', t: tr("Новый онбординг") },
  { x: 22, y: 52, r: 2, c: '#C9F2D4', t: tr("Сделать прототип") },
  { x: 52, y: 58, r: -3, c: '#E5D4FF', t: tr("Опрос пользователей") },
];

function BoardIllustration() {
  const reduce = useReducedMotion();
  return (
    <div className="canvas-dots relative hidden overflow-hidden border-l border-border lg:block" aria-hidden>
      <div className="absolute inset-0 bg-gradient-to-br from-accent-200/25 via-transparent to-transparent dark:from-accent-900/20" />
      {STICKIES.map((s, i) => (
        <motion.div
          key={s.t}
          initial={reduce ? false : { opacity: 0, y: 24, scale: 0.9, rotate: s.r * 2 }}
          animate={{ opacity: 1, y: 0, scale: 1, rotate: s.r }}
          transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.15 + i * 0.08 }}
          className="absolute flex size-40 items-center justify-center p-4 text-center text-[17px] leading-snug font-medium text-stone-800 shadow-[0_1px_1px_rgb(0_0_0/0.05),0_10px_24px_-8px_rgb(0_0_0/0.22)] xl:size-44"
          style={{ left: `${s.x}%`, top: `${s.y}%`, background: s.c, borderRadius: 4 }}
        >
          {s.t}
        </motion.div>
      ))}
      <motion.svg
        viewBox="0 0 200 120"
        className="absolute top-[40%] left-[38%] w-56 text-accent"
        initial={reduce ? false : { pathLength: 0, opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <motion.path
          d="M10 20 C 80 10, 120 110, 190 90"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="1 0"
          initial={reduce ? false : { pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.1, delay: 0.7, ease: 'easeOut' }}
        />
        <path d="M181 81 L 191 90 L 178 96" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </motion.svg>
      <Cursor name={tr("Мария")} color="#E5484D" x="62%" y="48%" delay={1} />
      <Cursor name={tr("Илья")} color="#0090FF" x="30%" y="78%" delay={1.3} />
    </div>
  );
}

function Cursor({ name, color, x, y, delay }: { name: string; color: string; x: string; y: string; delay: number }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className="absolute"
      style={{ left: x, top: y }}
      initial={reduce ? false : { opacity: 0, x: -30, y: 20 }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, x: [0, 18, 6, 0], y: [0, -10, 8, 0] }}
      transition={reduce ? {} : { opacity: { delay }, x: { delay, duration: 6, repeat: Infinity, ease: 'easeInOut' }, y: { delay, duration: 6, repeat: Infinity, ease: 'easeInOut' } }}
    >
      <svg width="20" height="22" viewBox="0 0 20 22" fill={color} stroke="white" strokeWidth="1.5">
        <path d="M2 2 L18 10 L10 12 L7 20 Z" strokeLinejoin="round" />
      </svg>
      <span className="ml-4 inline-block -translate-y-1 rounded-full px-2 py-0.5 text-xs font-semibold text-white shadow-md" style={{ background: color }}>
        {name}
      </span>
    </motion.div>
  );
}
